import { Component, Inject, OnDestroy, OnInit, inject } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import {
  FormBuilder,
  Validators,
  FormGroup,
  FormControl,
} from "@angular/forms";
import { debounceTime, distinctUntilChanged, takeUntil } from "rxjs/operators";
import { Subject } from "rxjs";
import { CommonService } from "src/app/services/common.service";
import { ApiService } from "src/app/services/api.service";
import endpoints from "src/app/helpers/endpoints";
import { HttpErrorResponse, HttpResponse } from "@angular/common/http";
import { ErrorType } from "src/app/enums/error-type.enum";
import { StorageService } from "src/app/services/storage.service";
import { MatTabChangeEvent } from "@angular/material/tabs";

enum CancelAgreementEnum {
  AgreementCancelRequest = "agreementCancelRequest",
  AgreementCancel = "agreementCancel",
}
@Component({
  selector: "fury-cancel-agreement-modal",
  templateUrl: "./cancel-agreement-modal.component.html",
  styleUrls: ["./cancel-agreement-modal.component.scss"],
})
export class CancelAgreementModalComponent implements OnInit, OnDestroy {
  ngUnsubscribe = new Subject<void>();
  cardType = "";
  stepIndex = 0;
  isUnsettled = false;
  expiryYear: number = Number(new Date().getFullYear().toString().slice(2, 4));
  month = Number(new Date().getMonth() + 1);
  cardDetailsForm = new FormGroup({
    cardHoldarName: new FormControl("", [Validators.required]),
    cardNumber: new FormControl("", [Validators.required]),
    expiryMonth: new FormControl("", [
      Validators.required,
      Validators.max(12),
      Validators.min(1),
    ]),
    expiryYear: new FormControl("", [
      Validators.required,
      Validators.min(this.expiryYear),
    ]),
    cvv: new FormControl("", [Validators.required]),
    transactionNumber: new FormControl(""),
    monthYear: new FormControl("", [Validators.required]),
  });
  bankDetailsForm = new FormGroup({
    routingNumber: new FormControl("", [
      Validators.required,
      Validators.minLength(9),
      Validators.maxLength(9),
    ]),
    accountNumber: new FormControl("", [
      Validators.required,
      Validators.minLength(4),
    ]),
    nameOnAccount: new FormControl("", [
      Validators.minLength(2),
      Validators.required,
    ]),
    bankName: new FormControl("", [Validators.required]),
    transactionNumber: new FormControl("", [Validators.required]),
  });
  chequeDetailForm = new FormGroup({
    checkNumber: new FormControl("", [
      Validators.required,
      Validators.minLength(4),
    ]),
  });
  private _formBuilder = inject(FormBuilder);
  firstFormGroup = this._formBuilder.group({
    firstCtrl: ["", Validators.required],
  });
  secondFormGroup = this._formBuilder.group({
    secondCtrl: ["", Validators.required],
  });
  details: any;
  billingId = "";
  agentId = this.storageService.getUser()?.userId;
  paymentTypeEnum = {
    0: "credit-debit-card",
    1: "bank-payment",
    2: "check-payment",
  };
  paymentType = this.paymentTypeEnum[0];
  bankandChequePaymentVisible = true;
  isLoading =false;

  constructor(
    public dialogRef: MatDialogRef<CancelAgreementModalComponent>,
    public commonService: CommonService,
    @Inject(MAT_DIALOG_DATA) public data: { agreementId: string, isShowSaveForLaterButton: boolean },
    private apiService: ApiService,
    private storageService: StorageService
  ) {}

  ngOnInit(): void {
    this.getKPIData();
    this.getTransactionDetail();
    this.cardDetailsForm
      .get("monthYear")
      ?.valueChanges.pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((res: string) => {
        if (res) {
          if (res.length === 4) {
            this.cardDetailsForm.patchValue({
              expiryMonth: res.substring(0, 2),
              expiryYear: res.substring(2, 4),
            });
            if (
              this.cardDetailsForm.get("expiryYear").value ===
                this.expiryYear.toString() &&
              Number(this.cardDetailsForm.get("expiryMonth").value) < this.month
            ) {
              this.cardDetailsForm
                .get("expiryMonth")
                .setErrors({ notValid: true });
            }
          }
        }
      });
  }

  getTransactionDetail(){
    this.apiService
      .get(endpoints.customerDashboard.getTransactionDetail(this.data.agreementId))
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (res: HttpResponse<any>) => {
          if (res.body) {
            this.isUnsettled = !!res.body?.isUnsettled;
          }
        },
        error: (err: HttpErrorResponse) => {
          this.apiService.alert(err.message, ErrorType.Error);
        },
      });
  }

  detectCardType(cardNumber: string) {
    this.cardType = this.commonService.getCardType(cardNumber);
  }
  close() {
    this.dialogRef.close();
  }
  getKPIData() {
    this.apiService
      .get(endpoints.customerDashboard.getAgreementStats(this.data.agreementId))
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (res: HttpResponse<any>) => {
          if (res.body) {
            this.details = res.body;
            if(this.details?.refundableToOriginalPaymentSource && this.details?.originalPaymentSource){
              this.bankandChequePaymentVisible = false;
              this.cardDetailsForm.patchValue({
                cardHoldarName: this.details?.originalPaymentSource?.cardHoldarName || '',
                cardNumber: this.details?.originalPaymentSource?.cardNumber,
                monthYear: this.details?.originalPaymentSource?.expiryMonth + this.details?.originalPaymentSource?.expiryYear?.substring(2, 4),
                cvv: this.details?.originalPaymentSource?.cvv
              })
            }
          }
        },
        error: (err: HttpErrorResponse) => {
          this.apiService.alert(err.message, ErrorType.Error);
        },
      });
  }
  checkPaymentDetails(actionType: string) {
    let payload: any;
    if(this.paymentType === this.paymentTypeEnum[0]){
      if(this.cardDetailsForm.invalid){
        this.cardDetailsForm.markAllAsTouched();
        return;
      }
      payload = {
        paymentDetail: {
        cardNumber: this.cardDetailsForm.value.cardNumber,
        cardHoldarName: this.cardDetailsForm.value.cardHoldarName,
        cvv: this.cardDetailsForm.value.cvv,
        expiryMonth: this.cardDetailsForm.value.expiryMonth,
        expiryYear: `20${this.cardDetailsForm.value.expiryYear}`
       },
       transactionNumber: this.cardDetailsForm.value.transactionNumber
      }
    }
    if(this.paymentType === this.paymentTypeEnum[1]){
      if(this.bankDetailsForm.invalid){
        this.bankDetailsForm.markAllAsTouched();
        return;
      }
      payload = {
        bankDetail: {
          bankName: this.bankDetailsForm.value.bankName,
          routingNumber: this.bankDetailsForm.value.routingNumber,
          accountNumber: this.bankDetailsForm.value.accountNumber,
          nameOnAccount: this.bankDetailsForm.value.nameOnAccount,
       },
       transactionNumber: this.bankDetailsForm.value.transactionNumber
      }
    }
    if(this.paymentType === this.paymentTypeEnum[2]){
      if(this.chequeDetailForm.invalid){
        this.chequeDetailForm.markAllAsTouched();
        return;
      }
      payload = {
        checkNumber: this.chequeDetailForm.value.checkNumber
      }
    }
    this.cancelAgreement(actionType, false, payload);
  }
  cancelAgreement(actionType: string, isComplete = false, payloadData?: any) {
    this.isLoading = true;
    let payload: any;
    if (actionType === CancelAgreementEnum.AgreementCancelRequest) {
      payload = { agentId: this.agentId };
    }
    if(actionType === CancelAgreementEnum.AgreementCancel){
      payload = payloadData;
      payload.agentId = this.agentId;
      payload.billingId = this.billingId;
      payload.paymentMethod = this.paymentType
    }
    this.apiService
      .post(
        endpoints.customerDashboard.cancelAgreement(
          this.data.agreementId,
          actionType
        ),
        payload
      )
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (res: HttpResponse<any>) => {
          if (res.body) {
            this.isLoading = false;
            if (
              isComplete &&
              actionType === CancelAgreementEnum.AgreementCancelRequest
            ) {
              this.apiService.alert(res.body?.message, ErrorType.Success);
              this.dialogRef.close(res);
              return;
            }
            if (
              !isComplete &&
              actionType === CancelAgreementEnum.AgreementCancelRequest
            ) {
              this.billingId = res.body?.billingId;
              this.stepIndex = 2;
              return;
            }
            if (actionType === CancelAgreementEnum.AgreementCancel) {
              this.apiService.alert(res.body?.message, ErrorType.Success);
              this.dialogRef.close(res);
            }
          }
        },
        error: (err: HttpErrorResponse) => {
          this.isLoading = false;
          this.apiService.alert(err.message, ErrorType.Error);
        },
      });
  }

  voidTransaction(){
    this.isLoading = true;
    const payload = { agentId: this.agentId };
    this.apiService
    .patch(endpoints.customerDashboard.cancelWithVoidTransaction(this.data.agreementId),payload)
    .pipe(takeUntil(this.ngUnsubscribe))
    .subscribe({
      next: (res: HttpResponse<any>) => {
        if (res.body) {
          this.isLoading = false;
          this.apiService.alert(res.body?.message, ErrorType.Success);
          this.dialogRef.close(res);
        }
      },
      error: (err: HttpErrorResponse) => {
        this.apiService.alert(err.message, ErrorType.Error);
      },
    });

  }
  onTabChange(event: MatTabChangeEvent) {
    this.paymentType = this.paymentTypeEnum[event.index];
  }
  getBankDetail() {
    if (this.bankDetailsForm.value.routingNumber.length !== 9) {
      this.bankDetailsForm.get("bankName").setValue("");
      return;
    }

    this.apiService
      .get(
        endpoints.customerDashboard.checkout.getBankDetail(
          this.bankDetailsForm.value.routingNumber
        )
      )
      .pipe(debounceTime(1000))
      .pipe(distinctUntilChanged())
      .subscribe(
        (res: any) => {
          if (res?.body?.bankName) {
            this.bankDetailsForm.get("bankName").setValue(res?.body?.bankName);
            return;
          }
          this.bankDetailsForm.get("bankName").setValue("");
        },
        (err) => {
          this.bankDetailsForm.get("bankName").setValue("");
          this.apiService.alert(err.error.message, ErrorType.Error);
        }
      );
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
